import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ExternalLink, History } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatApiError } from '@/lib/api';
import { DocumentPanel } from '@/components/shared/DocumentPanel';
import { bizDate, bizDateTime } from '@/lib/dates';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';

const STATUS_CLS = {
  compliant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  na: 'bg-muted text-muted-foreground',
  expired: 'bg-red-50 text-red-700 border-red-200',
};

export const WrDetailDrawer = ({ t, cell, canEdit, onClose, fetchDetail, saveRecord, onSaved,
                                 docMode = 'client', docCompanyId = null, docEnabled = true }) => {
  const { i18n } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!cell) { setDetail(null); setForm(null); return; }
    let alive = true;
    fetchDetail(cell.employee.id, cell.requirement.id).then((d) => {
      if (!alive) return;
      setDetail(d);
      const r = d.record || {};
      setForm({
        status: r.status || 'pending',
        document_date: r.document_date || '',
        expiration_mode: r.expiration_mode || '',
        expiration_date: r.expiration_date || '',
        location: r.location || '',
        url: r.url || '',
        observations: r.observations || '',
      });
    }).catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, [cell]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = (f) => ({
    ...f,
    document_date: f.document_date || null,
    expiration_mode: f.expiration_mode || null,
    expiration_date: f.expiration_date || null,
    location: f.location || null,
    url: f.url || null,
    observations: f.observations || null,
  });

  // V5.1 §B.2 — lets DocumentPanel attach files on cells without a saved
  // record: creates the audited record first, then returns its id.
  const resolveRecordId = async () => {
    const d = await saveRecord(cell.employee.id, cell.requirement.id, buildPayload(form));
    setDetail(d);
    onSaved?.();
    return d?.record?.id || null;
  };

  const save = async () => {
    setBusy(true);
    try {
      const d = await saveRecord(cell.employee.id, cell.requirement.id, buildPayload(form));
      setDetail(d);
      toast.success(t('wr.drawer.saved'));
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const req = cell?.requirement;
  const emp = cell?.employee;
  return (
    <Sheet open={!!cell} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto" data-testid="wr-detail-drawer">
        {!(cell && form) && (
          <SheetHeader className="sr-only">
            <SheetTitle>{t('wr.title')}</SheetTitle>
          </SheetHeader>
        )}
        {cell && form && (
          <>
            <SheetHeader className="text-left space-y-1">
              <SheetTitle className="text-xl">{req.name}</SheetTitle>
              <p className="text-base text-muted-foreground">
                {emp.name} · {[emp.department_name, emp.position].filter(Boolean).join(' · ') || '—'}
              </p>
              {detail && (
                <Badge variant="outline" className={`w-fit text-sm ${STATUS_CLS[detail.effective_status]}`} data-testid="wr-drawer-effective">
                  {t(`wr.status.${detail.effective_status}`)}
                </Badge>
              )}
            </SheetHeader>

            <div className="mt-6 space-y-5 text-base">
              <div className="space-y-2">
                <Label className="text-sm">{t('wr.drawer.status')}</Label>
                <RadioGroup value={form.status} onValueChange={(v) => set('status', v)}
                            className="flex flex-wrap gap-4" disabled={!canEdit}>
                  {['compliant', 'pending', 'na'].map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <RadioGroupItem value={s} id={`wr-st-${s}`} disabled={!canEdit} data-testid={`wr-drawer-status-${s}`} />
                      <Label htmlFor={`wr-st-${s}`} className="text-base font-normal cursor-pointer">{t(`wr.status.${s}`)}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">{t('wr.drawer.documentDate')}</Label>
                  {canEdit ? (
                    <LocalizedDateInput id="wr-docdate" value={form.document_date}
                                        onChange={(v) => set('document_date', v)}
                                        data-testid="wr-drawer-docdate" />
                  ) : (
                    <p className="text-base py-1.5" data-testid="wr-drawer-docdate">
                      {form.document_date ? bizDate(form.document_date, i18n.language) : '—'}
                    </p>
                  )}
                </div>
                {req.expires && (
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('wr.drawer.expiration')}</Label>
                    <RadioGroup value={form.expiration_mode} onValueChange={(v) => set('expiration_mode', v)}
                                className="flex flex-col gap-2 pt-1" disabled={!canEdit}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="expires" id="wr-exp-yes" disabled={!canEdit} data-testid="wr-drawer-exp-expires" />
                        <Label htmlFor="wr-exp-yes" className="text-base font-normal cursor-pointer">{t('wr.drawer.hasExpiration')}</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="does_not_expire" id="wr-exp-no" disabled={!canEdit} data-testid="wr-drawer-exp-never" />
                        <Label htmlFor="wr-exp-no" className="text-base font-normal cursor-pointer">{t('wr.drawer.doesNotExpire')}</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
              {req.expires && form.expiration_mode === 'expires' && (
                <div className="space-y-1.5">
                  <Label className="text-sm">{t('wr.drawer.expirationDate')}</Label>
                  {canEdit ? (
                    <LocalizedDateInput id="wr-expdate" value={form.expiration_date}
                                        onChange={(v) => set('expiration_date', v)}
                                        className="sm:max-w-[240px]"
                                        data-testid="wr-drawer-expdate" />
                  ) : (
                    <p className="text-base py-1.5" data-testid="wr-drawer-expdate">
                      {form.expiration_date ? bizDate(form.expiration_date, i18n.language) : '—'}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm">{t('wr.drawer.location')}</Label>
                <Input className="md:text-base" value={form.location}
                       onChange={(e) => set('location', e.target.value)}
                       placeholder={t('wr.drawer.locationPh')} disabled={!canEdit}
                       data-testid="wr-drawer-location" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('wr.drawer.url')}</Label>
                <div className="flex gap-2">
                  <Input className="md:text-base" value={form.url}
                         onChange={(e) => set('url', e.target.value)}
                         placeholder="https://…" disabled={!canEdit} data-testid="wr-drawer-url" />
                  {form.url && (
                    <Button variant="outline" size="icon" asChild data-testid="wr-drawer-open-url">
                      <a href={form.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{t('wr.drawer.urlHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t('wr.drawer.observations')}</Label>
                <Textarea rows={3} className="md:text-base" value={form.observations}
                          onChange={(e) => set('observations', e.target.value)}
                          disabled={!canEdit} data-testid="wr-drawer-observations" />
              </div>

              {canEdit ? (
                <Button className="rounded-full" onClick={save} disabled={busy} data-testid="wr-drawer-save">
                  {t('wr.drawer.save')}
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground italic">{t('wr.drawer.readOnly')}</p>
              )}

              <div className="pt-5 border-t space-y-3" data-testid="wr-drawer-history">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <History className="h-4 w-4" /> {t('wr.drawer.history')}
                </p>
                {(detail?.history || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('wr.drawer.historyEmpty')}</p>
                )}
                {(detail?.history || []).map((h) => (
                  <div key={h.id} className="text-sm rounded-lg border bg-muted/30 p-3 space-y-1">
                    <p className="font-medium">{h.actor_name} <span className="text-muted-foreground font-normal">· {bizDateTime(h.timestamp, i18n.language)}</span></p>
                    {(h.changes || []).map((c, i) => (
                      <p key={i} className="text-muted-foreground">
                        {t(`wr.fields.${c.field}`, { defaultValue: c.field })}: {String(c.old ?? '—')} → <b className="text-foreground">{String(c.new ?? '—')}</b>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {/* V5.0 §9.1 / V5.1 §B.2 — secure document attachments; always
                visible so Client Admin can upload even before a record exists */}
            {detail && docEnabled && (
              <DocumentPanel suite="workforce_records" entityType="wr_record"
                             entityId={detail?.record?.id || null}
                             resolveEntityId={canEdit && docMode === 'client' ? resolveRecordId : null}
                             readOnly={!canEdit}
                             mode={docMode} companyId={docCompanyId}
                             employeeUserId={cell?.employee?.id}
                             defaultDocType="certification"
                             docTypes={["identification", "contract", "certification",
                                        "license", "regulatory", "work_permit",
                                        "training_certificate", "other"]}
                             compact />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
